import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "@/components/ui/sonner";
import { DemoRibbon } from "@/components/DemoRibbon";
import { CookieConsent } from "@/components/CookieConsent";
import { AuthProvider } from "@/lib/AuthContext";
import { trackPageview } from "@/lib/analytics";
import Home from "@/pages/Home";
import Preview from "@/pages/Preview";
import Demo from "@/pages/Demo";
import Team from "@/pages/Team";
import CampaignDetail from "@/pages/CampaignDetail";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import FoundingDonors from "@/pages/FoundingDonors";
import FoundingEdit from "@/pages/FoundingEdit";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import RecurringGifts from "@/pages/RecurringGifts";
import AuthCallback from "@/pages/AuthCallback";
import VerifyEdu from "@/pages/VerifyEdu";
import AdminReview from "@/pages/AdminReview";
import AdminFounding from "@/pages/AdminFounding";
import AdminTeam from "@/pages/AdminTeam";
import AdminLaunch from "@/pages/AdminLaunch";
import LiveCampaign from "@/pages/LiveCampaign";
import BrowseCampaigns from "@/pages/BrowseCampaigns";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import StudentsComingSoon from "@/pages/StudentsComingSoon";
import LaunchChecklist from "@/pages/LaunchChecklist";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import DemoStory from "@/pages/DemoStory";
import DemoAdminStory from "@/pages/DemoAdminStory";
import DemoDonorStory from "@/pages/DemoDonorStory";
import DemoEndSlatePreview from "@/pages/DemoEndSlatePreview";
import NecklacePreview from "@/pages/NecklacePreview";
import TypographyConcepts from "@/pages/TypographyConcepts";
import NecklaceScrollMockup from "@/pages/NecklaceScrollMockup";
import ImpactBandMockup from "@/pages/ImpactBandMockup";
import SustainingDonorsMockup from "@/pages/SustainingDonorsMockup";
import OnboardingReturnMockup from "@/pages/OnboardingReturnMockup";
import Contact from "@/pages/Contact";
import FAQPage from "@/pages/FAQPage";
import RollAdd from "@/pages/RollAdd";
import NotFound from "@/pages/NotFound";
import ComingSoon from "@/pages/ComingSoon";
import { isMaintenanceOn, pathIsAllowlisted } from "@/lib/maintenance";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Don't scroll during OAuth callback — AuthCallback handles the fragment.
    if (hash?.includes("session_id=")) return;
    if (hash) {
      const id = hash.slice(1);
      const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

/**
 * RouteAnalytics — fires a single GA4 `page_view` per SPA navigation.
 * The gtag config in index.html has `send_page_view: false` so this
 * component owns 100% of pageview reporting (including the first paint,
 * which useEffect runs on mount).
 */
function RouteAnalytics() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Skip the OAuth callback fragment step — it's a transient redirect
    // and would double-count against the destination page.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) return;
    trackPageview(`${pathname}${search || ""}`);
  }, [pathname, search]);
  return null;
}

/**
 * OAuth fragment router — intercepts `#session_id=...` synchronously
 * during render (per Emergent playbook: NOT inside a useEffect, else
 * ProtectedRoute wins the race and 401s /auth/me first).
 */
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Sitewide maintenance curtain — env-flagged. Admin routes stay
  // reachable (see /app/frontend/src/lib/maintenance.js allowlist).
  if (isMaintenanceOn && !pathIsAllowlisted(location.pathname)) {
    return (
      <ComingSoon
        testid="maintenance-page"
        eyebrow="Under maintenance"
        title="We're back in a few minutes."
        body="Small tune-up in progress. Nothing's lost — the site will be right back. Refresh in a moment and we'll be here."
        primaryLabel="Try again"
        onPrimaryClick={() => window.location.reload()}
      />
    );
  }

  return (
    <Routes>
      {/* Day-one clean marketing site */}
      <Route path="/" element={<Home />} />
      <Route path="/campaigns" element={<BrowseCampaigns />} />
      <Route path="/preview" element={<Preview />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/recurring-gifts" element={<RecurringGifts />} />
      <Route path="/verify/edu" element={<VerifyEdu />} />
      <Route path="/admin" element={<AdminReview />} />
      <Route path="/admin/founding" element={<AdminFounding />} />
      <Route path="/admin/team" element={<AdminTeam />} />
      <Route path="/admin/launch" element={<AdminLaunch />} />

      {/* Shared pages (real content) */}
      {/*
        `/team` page.

        Env-flagged: while the team page is a draft-only preview for board
        review, we hide it in production behind the generic ComingSoon.
        Flip `REACT_APP_TEAM_PAGE_LIVE=true` in Emergent Secrets and
        Republish to reveal the real page on myrichaunt.org.

          preview .env → true   → real Team page (with partial bios)
          production   → unset  → ComingSoon placeholder
      */}
      <Route
        path="/team"
        element={
          String(process.env.REACT_APP_TEAM_PAGE_LIVE || "").toLowerCase() === "true" ? (
            <Team />
          ) : (
            <ComingSoon
              eyebrow="Team page coming soon"
              title="Meet the team — soon."
              body="We're putting the finishing touches on the bios of the folks behind My Rich Aunt. Come back in a few days and you'll meet everyone properly."
            />
          )
        }
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/founding" element={<FoundingDonors />} />
      <Route path="/founding/edit/:token" element={<FoundingEdit />} />

      {/* Real live campaigns (Mongo-backed) */}
      <Route path="/campaign/:id" element={<LiveCampaign />} />

      {/* Student campaigns gate — flip REACT_APP_STUDENT_CAMPAIGNS_OPEN
          to open the platform. Sits at a stable URL so Preview + Register
          can redirect here. */}
      <Route path="/students-coming-soon" element={<StudentsComingSoon />} />

      {/* Ops-only printable launch checklist. Noindex, unlinked. */}
      <Route path="/launch-checklist" element={<LaunchChecklist />} />

      {/* Generic "Coming soon" utility page — reusable to temporarily
          hide any route from the public. Also lives at a stable URL so
          admins can share the placeholder link if they need to. */}
      <Route path="/coming-soon" element={<ComingSoon />} />

      {/* Stripe Checkout return URLs — success + cancel. Auto-noindex. */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      {/* Contact form */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/roll/add/:token" element={<RollAdd />} />

      {/* Prototype namespace — all mocked flows */}
      <Route path="/demo" element={<Demo />} />
      <Route path="/demo/story" element={<DemoStory />} />
      <Route path="/demo/donor-story" element={<DemoDonorStory />} />
      <Route path="/demo/admin-story" element={<DemoAdminStory />} />
      <Route path="/demo/end-slate-preview" element={<DemoEndSlatePreview />} />
      <Route path="/demo/necklace-preview" element={<NecklacePreview />} />
      <Route path="/demo/typography-concepts" element={<TypographyConcepts />} />
      <Route path="/demo/necklace-scroll" element={<NecklaceScrollMockup />} />
      <Route path="/demo/impact-band" element={<ImpactBandMockup />} />
      <Route path="/demo/sustaining-donors" element={<SustainingDonorsMockup />} />
      <Route path="/demo/onboarding-return" element={<OnboardingReturnMockup />} />
      <Route path="/demo/campaign/:id" element={<CampaignDetail />} />
      <Route path="/demo/admin" element={<Admin />} />

      {/* Vanity links — myrichaunt.org/{first-name}; falls back to the 404 page when no campaign matches */}
      <Route path="/:slug" element={<LiveCampaign />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="App grain">
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <RouteAnalytics />
            <AppRoutes />
            <DemoRibbon />
            <CookieConsent />
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="bottom-center" />
      </div>
    </ReactLenis>
  );
}

export default App;
